import { type SiteId } from '@solinkcorp/api-types';
import {
  generateVideoDiscoveryURL,
  HISTOGRAM_RANGE,
  type HistogramRange,
  type SearchOperator,
} from '@solinkcorp/vd-common';
import { type IEvent } from 'clients/PlatformApi';
import { ROUTE_ALARMS, ROUTE_CLIPS, ROUTE_EVENTS, SOURCE_LINK } from 'vd-constants';

/**
 * The event fields needed to build a share/deep link. A narrow `Pick` so the
 * function stays pure and trivially testable without a full `IEvent`.
 */
type ShareLinkEvent = Pick<IEvent, 'id' | 'startTime' | 'type' | 'subtype'>;

/** The event-filter booleans used when building an Events-page deep link. */
interface ShareLinkEventFilterState {
  transaction?: boolean;
  motion?: boolean;
  bookmark?: boolean;
}

export interface BuildEventShareUrlParams {
  event: ShareLinkEvent;
  /** Usually `window.location.origin`; passed in to keep this pure. */
  origin: string;
  tenantId: string;
  /** Selected site IDs (maps to `siteIds`). */
  locationIds: readonly SiteId[] | undefined;
  /**
   * First site's first device ID (maps to `locationId`, used to support old
   * mobile apps). Resolved by the caller from its devices source.
   */
  firstSiteFirstDeviceId: string | undefined;
  /** Build an Alarms-page link. Takes precedence over `isClips`. */
  isAlarms?: boolean;
  /** Build a Clips-page link. */
  isClips?: boolean;
  // --- Events-page link extras (ignored for the alarms/clips routes) ---
  eventFilterState: ShareLinkEventFilterState;
  searchString?: string;
  showRevenue?: boolean;
  searchCapsules?: readonly string[];
  selectedReportId?: string;
  histogramRange: HistogramRange;
  feature?: string;
  searchOperator?: SearchOperator;
}

/**
 * Pure construction of an event share/deep link.
 *
 * Extracted from `useEventMenuActions.getShareUrl` so the URL logic no longer
 * reads from `window`, redux, or flux stores directly: callers gather the
 * inputs from whatever surrounding state they have (the Events page from its
 * redux/EventFilterStore state; the Native Data Explorer from its own filters)
 * and pass them in. This removes the hidden global-state coupling that made the
 * link depend on the Events page having been visited, and makes the behaviour
 * unit-testable in isolation.
 *
 * Behaviour mirrors the original exactly: an Alarms link when `isAlarms`, a
 * Clips link when `isClips`, otherwise an Events link; when the event has a
 * `type` the link filters to that event type rather than the generic
 * transaction/motion/bookmark filter set; and the `HOUR` histogram range is
 * widened to `DAY` for the Events link.
 */
export function buildEventShareUrl({
  event,
  origin,
  tenantId,
  locationIds,
  firstSiteFirstDeviceId,
  isAlarms,
  isClips,
  eventFilterState,
  searchString,
  showRevenue,
  searchCapsules,
  selectedReportId,
  histogramRange,
  feature,
  searchOperator,
}: BuildEventShareUrlParams): string {
  const { id: eventId, startTime: date, type: eventType, subtype: eventSubType } = event;

  const eventFilter = eventType ? { eventType, eventSubType } : undefined;

  // the event card is displayed on the Alarms page
  if (isAlarms) {
    return generateVideoDiscoveryURL({
      origin,
      date,
      eventId,
      eventFilter,
      tenantId,
      siteIds: locationIds,
      // locationId = deviceId, used to support old mobile apps
      locationId: firstSiteFirstDeviceId,
      route: ROUTE_ALARMS,
      source: SOURCE_LINK,
    });
  }

  // the event card is displayed on the Clips page
  if (isClips) {
    return generateVideoDiscoveryURL({
      origin,
      date,
      eventId,
      tenantId,
      siteIds: locationIds,
      // locationId = deviceId, used to support old mobile apps
      locationId: firstSiteFirstDeviceId,
      route: ROUTE_CLIPS,
      source: SOURCE_LINK,
    });
  }

  return generateVideoDiscoveryURL({
    origin,
    eventFilter: eventType
      ? eventFilter
      : {
          transaction: eventFilterState.transaction,
          motion: eventFilterState.motion,
          bookmark: eventFilterState.bookmark,
        },
    date,
    eventId,
    tenantId,
    searchString,
    showRevenue,
    capsules: searchCapsules,
    siteIds: locationIds,
    // locationId = deviceId, used to support old mobile apps
    locationId: firstSiteFirstDeviceId,
    reportId: selectedReportId,
    range: histogramRange === HISTOGRAM_RANGE.HOUR ? HISTOGRAM_RANGE.DAY : histogramRange,
    route: ROUTE_EVENTS,
    source: SOURCE_LINK,
    feature,
    searchOperator,
  });
}
